/**
 * Chip — FreshVibe Way
 * Lightweight pill navigation element
 *
 * States: inactive | active | elevated
 * Supports: color variants, long-press, icon dot, + variant
 *
 * Universal ChipNav rules:
 * - Never horizontal scroll — use overflow flyout instead
 * - Chips that don't fit collapse into overflow
 * - Pinned chips always visible, contextual chips appear dynamically
 */
import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Box, Text } from '@mantine/core';

export type ChipVariant = 'default' | 'status' | 'tag' | 'add';
export type ChipState = 'inactive' | 'active' | 'elevated';

export interface ChipProps {
  /** Label text */
  label: string;
  /** Visual state */
  state?: ChipState;
  /** Color for status chips — sets the dot color */
  color?: string;
  /** Chip variant */
  variant?: ChipVariant;
  /** Click handler */
  onClick?: (e: React.MouseEvent) => void;
  /** Long-press handler (for context menu) */
  onLongPress?: (e: React.MouseEvent) => void;
  /** Disable interactions */
  disabled?: boolean;
  /** Extra className */
  className?: string;
  /** Tooltip */
  title?: string;
  /** Custom inline style */
  style?: React.CSSProperties;
}

const LONG_PRESS_DELAY = 400;

export const Chip: React.FC<ChipProps> = ({
  label,
  state = 'inactive',
  color,
  variant = 'default',
  onClick,
  onLongPress,
  disabled = false,
  className = '',
  title,
  style,
}) => {
  const longPressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLongPressing = React.useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (disabled || !onLongPress) return;
    isLongPressing.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPressing.current = true;
      onLongPress(e);
    }, LONG_PRESS_DELAY);
  }, [disabled, onLongPress]);

  const handleMouseUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (isLongPressing.current) {
      isLongPressing.current = false;
      return; // swallow click after long-press
    }
    onClick?.(e);
  }, [onClick]);

  const isActive = state === 'active';
  const isElevated = state === 'elevated';
  const isAdd = variant === 'add';

  return (
    <Box
      component="button"
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleMouseDown}
      onTouchEnd={handleMouseUp}
      disabled={disabled}
      title={title}
      className={`fc-chip ${isActive ? 'active' : ''} ${isElevated ? 'elevated' : ''} ${isAdd ? 'fc-chip--add' : ''} ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 'var(--fc-chip-height)',
        padding: '0 var(--fc-chip-padding-x)',
        fontSize: 'var(--fc-chip-font-size)',
        fontWeight: 500,
        fontFamily: 'inherit',
        borderRadius: 'var(--fc-chip-radius)',
        border: isAdd
          ? '1px dashed var(--fc-border-secondary)'
          : '1px solid var(--fc-chip-border-inactive)',
        background: isActive
          ? 'var(--fc-chip-bg-active)'
          : isElevated
          ? 'var(--fc-chip-bg-elevated)'
          : isAdd
          ? 'transparent'
          : 'var(--fc-chip-bg-inactive)',
        color: isActive
          ? 'var(--fc-chip-text-active)'
          : 'var(--fc-chip-text-inactive)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.5 : 1,
        transition: 'all var(--fc-motion-fast)',
        boxShadow: isElevated ? 'var(--fc-shadow-sm)' : 'none',
        flexShrink: 0,
        ...style,
      } as React.CSSProperties}
    >
      {color && (
        <Box
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: color,
            flexShrink: 0,
          }}
        />
      )}
      <Text size="xs" fw={500} style={{ lineHeight: 1 }}>
        {label}
      </Text>
    </Box>
  );
};

/** Overflow count badge — shown on the overflow chip */
export const OverflowChip: React.FC<{
  count: number;
  onClick: () => void;
}> = ({ count, onClick }) => (
  <Chip
    label={`+${count}`}
    variant="add"
    onClick={onClick}
    style={{ fontSize: 'var(--fc-chip-font-size)' }}
  />
);

/** ChipRow — Universal ChipNav pattern
 *
 * Key rules:
 * - overflowX: hidden — NO horizontal scroll, ever
 * - flexWrap: wrap — chips wrap to next line if needed
 * - Overflow chips collapse into a flyout
 *
 * Usage:
 *   <ChipRow>
 *     {pinnedChips}
 *     {overflowCount > 0 && <OverflowChip count={overflowCount} onClick={openFlyout} />}
 *   </ChipRow>
 */
export interface ChipRowProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Wrap chips if they don't fit (default: wrap) */
  wrap?: boolean;
}

export const ChipRow: React.FC<ChipRowProps> = ({
  children,
  className = '',
  style,
  wrap = true,
}) => (
  <Box
    className={`fc-chip-row ${className}`}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      overflowX: 'hidden',     // Universal ChipNav rule: NO horizontal scroll
      overflowY: 'visible',     // Allow chips to wrap
      flexWrap: wrap ? 'wrap' : 'nowrap',
      scrollbarWidth: 'none',
      paddingBottom: 2,
      minHeight: 'var(--fc-chip-height)',
      ...style,
    } as React.CSSProperties}
  >
    {children}
  </Box>
);

/** ChipFlyout — dropdown for overflow chips
 *
 * Renders a positioned dropdown with chip options.
 * Closes on outside click.
 */
export interface ChipFlyoutProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  anchorRef?: React.RefObject<HTMLElement>;
  align?: 'left' | 'right';
}

export const ChipFlyout: React.FC<ChipFlyoutProps> = ({
  open,
  onClose,
  children,
  anchorRef,
  align = 'left',
}) => {
  const flyoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        flyoutRef.current && !flyoutRef.current.contains(e.target as Node) &&
        anchorRef?.current && !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return (
    <Box
      ref={flyoutRef}
      className="fc-chip-flyout"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: align === 'left' ? 0 : 'auto',
        right: align === 'right' ? 0 : 'auto',
        background: 'var(--fc-bg-primary)',
        border: '1px solid var(--fc-border-primary)',
        borderRadius: 'var(--fc-radius-md)',
        boxShadow: 'var(--fc-shadow-md)',
        padding: '6px 8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        zIndex: 200,
        minWidth: 120,
        animation: 'fc-scale-in var(--fc-motion-fast) forwards',
      } as React.CSSProperties}
    >
      {children}
    </Box>
  );
};